import React, { FC, useEffect, useState } from "react";
import { EllipsisVertical, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import usePostData from "../../hooks/usePostData";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/hooks/useAuth";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";

const readCreatorId = (contact: any): number | null => {
  const raw = contact?.creator?.id ?? contact?.creatorId ?? null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const ContactChoice: FC<{
  financer: any;
  contacts: any[];
  metaCarrier?: number | null;
  handleChange: (value) => void;
  selected?: any;
  className?: string;
  withActions?: boolean;
  onContactsChange?: (contacts: any[]) => void;
}> = ({
  financer,
  contacts,
  metaCarrier = null,
  handleChange,
  selected = null,
  className = "",
  withActions = false,
  onContactsChange,
}) => {
  const { user } = useAuth();
  const [contactsState, setContactsState] = useState(contacts);
  const updateContactsState = (
    updater: (currentContacts: any[]) => any[],
  ) => {
    setContactsState((currentContacts) => {
      const nextContacts = updater(currentContacts);
      onContactsChange?.(nextContacts);
      return nextContacts;
    });
  };

  useEffect(() => {
    const creatorId = Number(user?.coworker ? metaCarrier : user?.id);
    if (!Array.isArray(contacts)) {
      setContactsState([]);
      return;
    }

    if (!Number.isFinite(creatorId) || creatorId <= 0) {
      setContactsState(contacts);
      return;
    }

    const filteredContacts = contacts.filter((contact) => {
      const contactCreatorId = readCreatorId(contact);
      return contactCreatorId === null || contactCreatorId === creatorId;
    });

    setContactsState(filteredContacts);
  }, [contacts, user, metaCarrier]);

  return (
    <div
      className={
        "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 " +
        className
      }
    >
      {contactsState.map((contact) => (
        <ContactCard
          key={contact.id}
          onClick={() => handleChange(contact)}
          contact={contact}
          isSelected={contact.id === selected?.id}
          withActions={withActions}
          onDeleted={(deletedContact) => {
            const deletedId = deletedContact?.id ?? deletedContact;
            updateContactsState((currentContacts) =>
              currentContacts.filter((c) => c.id !== deletedId),
            );
            if (selected?.id === deletedId) {
              handleChange(null);
            }
          }}
          onDeleteError={(deletedContact) => {
            updateContactsState((currentContacts) => {
              const alreadyExists = currentContacts.some(
                (currentContact) => currentContact.id === deletedContact.id,
              );
              return alreadyExists
                ? currentContacts
                : [...currentContacts, deletedContact];
            });
          }}
          onUpdate={(res) => {
            updateContactsState((currentContacts) =>
              currentContacts.map((c) => (c.id === res.id ? res : c)),
            );
            if (selected?.id === res.id) {
              handleChange(res);
            }
          }}
          onUpdateError={(previousContact) => {
            updateContactsState((currentContacts) =>
              currentContacts.map((c) =>
                c.id === previousContact.id ? previousContact : c,
              ),
            );
            if (selected?.id === previousContact.id) {
              handleChange(previousContact);
            }
          }}
        />
      ))}
      <ContactAdd
        financer={financer}
        handleCreate={(contact) => {
          updateContactsState((currentContacts) => [
            ...currentContacts,
            contact,
          ]);
          handleChange(contact);
          return contact.id;
        }}
        handleCreateSuccess={(contact, optimisticId) => {
          updateContactsState((currentContacts) => {
            const hasOptimisticContact = currentContacts.some(
              (currentContact) => currentContact.id === optimisticId
            );
            return hasOptimisticContact
              ? currentContacts.map((currentContact) =>
                  currentContact.id === optimisticId ? contact : currentContact
                )
              : [...currentContacts, contact];
          });
          handleChange(contact);
        }}
        handleCreateError={(optimisticId) => {
          updateContactsState((currentContacts) =>
            currentContacts.filter((contact) => contact.id !== optimisticId),
          );
          if (selected?.id === optimisticId) {
            handleChange(null);
          }
        }}
      />
    </div>
  );
};

const ContactCard = ({
  onClick,
  contact,
  isSelected,
  withActions = false,
  onDeleted = null,
  onDeleteError = null,
  onUpdate = null,
  onUpdateError = null,
}) => {
  const { firstname, lastname, phone, email } = contact;

  const { t } = useTranslation("dashboard", {
    keyPrefix: "financeur",
  });

  const [openEdit, setOpenEdit] = useState(false);
  const { postData } = usePostData();
  const hidePostActions = useHidePostActions();

  return (
    <div
      onClick={() => {
        onClick(contact);
      }}
      className={
        "relative cursor-pointer bg-white shadow-md rounded-2xl p-4 w-full max-w-md mx-auto " +
        (isSelected ? "border-2 border-primary" : "")
      }
    >
      <h2 className="text-xl font-semibold text-gray-800 truncate max-w-[93%]">
        {firstname} {lastname}
      </h2>
      {phone && (
        <p className="text-gray-600 mt-2">
          📞 <span className="font-mono">{phone}</span>
        </p>
      )}
      {email && (
        <p className="text-gray-600 mt-1">
          📧 <span className="font-mono">{email}</span>
        </p>
      )}
      {withActions && (
        <div className="absolute right-2 top-5">
          {!hidePostActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="cursor-pointer">
                <EllipsisVertical size={18} className="text-gray-800" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="z-[99999]">
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenEdit(true);
                    }}
                  >
                    <span>{t("Modifier")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onDeleted) {
                        (onDeleted as any)(contact);
                      }
                      postData({
                        url:
                          import.meta.env.VITE_API_ENDPOINT +
                          "/representant/delete/" +
                          contact.id,
                        data: {},
                      }).then((res) => {
                        if (!res && onDeleteError) {
                          (onDeleteError as any)(contact);
                        }
                      });
                    }}
                  >
                    <span>{t("Supprimer")}</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      {!hidePostActions && (
        <ContactEdit
          open={openEdit}
          setOpen={setOpenEdit}
          contact={contact}
          handleSave={onUpdate}
          handleSaveError={onUpdateError}
        />
      )}
    </div>
  );
};

const ContactAdd = ({
  financer,
  handleCreate,
  handleCreateSuccess,
  handleCreateError,
}) => {
  const [open, setOpen] = useState(false);
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const { postData } = usePostData<any>();
  const [loadingAdd, setLoadingAdd] = useState(false);
  const hidePostActions = useHidePostActions();

  useEffect(() => {
    setFirstname("");
    setLastname("");
    setPhone("");
    setEmail("");

    if (open) {
      if (document.getElementById("addFinancementContent")) {
        document.getElementById("addFinancementContent").style.opacity = "0.4";
      }
    } else {
      if (document.getElementById("addFinancementContent")) {
        document.getElementById("addFinancementContent").style.opacity = "1";
      }
    }
  }, [open]);

  if (hidePostActions) return null;
  if (open) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[800px] w-[800px] min-w-[800px]">
          <div className="space-y-6">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                Ajouter un représentant
              </DialogTitle>
              <DialogDescription>
                Renseigner les informations du representant.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-900">
                  Prénom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  className="mt-1 p-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-900">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  className="mt-1 p-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-900">
                  Téléphone
                </label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1 p-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-900">
                  Email
                </label>
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 p-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <DialogFooter>
              {!hidePostActions && (
                <Button
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    setLoadingAdd(true);

                    if (firstname === "" || lastname === "") {
                      setLoadingAdd(false);
                      return;
                    }

                    const optimisticContact = {
                      id: `optimistic-representant-${Date.now()}`,
                      firstname,
                      lastname,
                      phone,
                      email,
                      user: financer,
                      userId: financer?.id,
                    };
                    const optimisticId = handleCreate(optimisticContact);
                    setOpen(false);

                    const res = await postData({
                      url: `${import.meta.env.VITE_API_ENDPOINT}/representant`,
                      data: {
                        firstname,
                        lastname,
                        phone,
                        email,
                        financer: financer.id,
                      },
                    });

                    if (res) {
                      setLoadingAdd(false);
                      handleCreateSuccess(res, optimisticId);
                      return;
                    }

                    handleCreateError(optimisticId);
                    setLoadingAdd(false);
                  }}
                  disabled={loadingAdd}
                >
                  Sauvegarder
                </Button>
              )}
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div
      onClick={() => setOpen(!open)}
      className="cursor-pointer bg-white shadow-md rounded-2xl p-4 w-full max-w-md mx-auto flex items-center justify-center"
    >
      <Plus size={50} className="text-4xl text-[#D4D4D4]" />
    </div>
  );
};

const ContactEdit = ({ open, setOpen, contact, handleSave, handleSaveError }) => {
  const [firstname, setFirstname] = useState(contact.firstname);
  const [lastname, setLastname] = useState(contact.lastname);
  const [phone, setPhone] = useState(contact.phone);
  const [email, setEmail] = useState(contact.email);
  const { postData } = usePostData<any>();
  const [loadingEdit, setLoadingEdit] = useState(false);
  const hidePostActions = useHidePostActions();

  useEffect(() => {
    setFirstname(contact.firstname);
    setLastname(contact.lastname);
    setPhone(contact.phone);
    setEmail(contact.email);

    if (open) {
      if (document.getElementById("addFinancementContent")) {
        document.getElementById("addFinancementContent").style.opacity = "0.4";
      }
    } else {
      if (document.getElementById("addFinancementContent")) {
        document.getElementById("addFinancementContent").style.opacity = "1";
      }
    }
  }, [open]);

  if (hidePostActions) return null;
  if (open) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[800px] w-[800px] min-w-[800px]">
          <div className="space-y-6">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                Modifier un représentant
              </DialogTitle>
              <DialogDescription>
                Renseigner les informations du representant.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-900">
                  Prénom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  className="mt-1 p-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-900">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  className="mt-1 p-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-900">
                  Téléphone
                </label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1 p-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-900">
                  Email
                </label>
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 p-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <DialogFooter>
              {!hidePostActions && (
                <Button
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    setLoadingEdit(true);

                    if (firstname === "" || lastname === "") {
                      setLoadingEdit(false);
                      return;
                    }

                    const optimisticContact = {
                      ...contact,
                      firstname,
                      lastname,
                      phone,
                      email,
                    };
                    handleSave?.(optimisticContact);
                    setOpen(false);

                    const res = await postData({
                      url: `${import.meta.env.VITE_API_ENDPOINT}/representant/${
                        contact.id
                      }`,
                      data: {
                        firstname,
                        lastname,
                        phone,
                        email,
                      },
                    });

                    if (res) {
                      setLoadingEdit(false);
                      handleSave(res);
                      return;
                    }

                    handleSaveError?.(contact);
                    setLoadingEdit(false);
                  }}
                  disabled={loadingEdit}
                >
                  Sauvegarder
                </Button>
              )}
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return <></>;
};

export default ContactChoice;
